import styled from "styled-components";
import { darken } from "polished";

export const ResultItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  padding-bottom: ${({ theme }) => theme.spacing.md};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  position: relative;
  width: 100%;

  &:last-child {
    border-bottom: none;
  }
`;

export const ResultLabel = styled.div<{ $isRequired?: boolean }>`
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.8rem;
  margin: 0 0 8px 0;
  color: ${({ theme }) => theme.colors.verae[600]};
  ${({ $isRequired, theme }) => $isRequired && `color: ${theme.colors.error};`}
`;

export const ResultValue = styled.div<{
  $editable?: boolean;
  $isEmpty?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  color: ${({ theme, $isEmpty }) =>
    $isEmpty ? theme.colors.textMuted : theme.colors.text};
  font-size: 1.1rem;
  margin-top: 6px;
  min-height: 24px;
  word-break: break-word;
  overflow-wrap: break-word;
  max-width: calc(100% - 60px);
  ${({ $editable, theme }) =>
    $editable &&
    `
    border-bottom: 1px dashed ${theme.colors.border};
    cursor: pointer;
    padding-bottom: 5px;
    transition: all 0.2s ease;
    
    &:hover {
      border-bottom-color: ${theme.colors.verae[400]};
      background-color: ${theme.colors.verae[50]};
    }
  `}
`;

export const EditableResultValue = styled.div`
  flex: 1;
  margin-top: 6px;
  width: 100%;
  display: flex;
  gap: 8px;
  max-width: 100%;
  overflow: hidden;
`;

export const ActionButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.verae[500]};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.verae[50]};
  }

  &:focus {
    outline: none;
  }
`;

export const DeleteButton = styled(ActionButton)`
  position: absolute;
  right: 0;
  top: calc(50% - 5px);
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.textMuted};
  z-index: 1;
`;

export const AddFieldButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  margin: 30px 0 10px 0;
  color: ${({ theme }) => theme.colors.verae[500]};
  cursor: pointer;
  width: 100%;

  &:hover {
    color: ${({ theme }) => darken(0.3, theme.colors.secondary)};
  }
`;

export const CustomFieldContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

export const CustomFieldRow = styled.div`
  display: flex;
  gap: 8px;
`;

export const CustomFieldLabel = styled.div`
  flex: 1;
`;

export const CustomFieldValue = styled.div`
  flex: 2;
`;

export const CustomFieldActions = styled.div`
  display: flex;
  align-items: center;
`;

export const RequiredIndicator = styled.span`
  color: ${({ theme }) => theme.colors.error};
  margin-left: 4px;
  font-size: 0.9rem;
`;

export const ConfidenceContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.verae[50]};
  padding: 20px 20px 25px 20px;
  border-radius: 8px;
  /* border: 1px solid ${({ theme }) => theme.colors.border}; */
`;

export const ConfidenceBar = styled.div`
  height: 8px;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.background};
  border-radius: 4px;
  overflow: hidden;
  margin-top: ${({ theme }) => theme.spacing.xs};
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

export const ConfidenceFill = styled.div<{ $confidence: number }>`
  height: 100%;
  width: ${({ $confidence }) => `${$confidence}%`};
  background-color: ${({ $confidence, theme }) =>
    $confidence < 40
      ? theme.colors.error
      : $confidence < 70
      ? theme.colors.blue
      : theme.colors.success};
  border-radius: 4px;
  transition: width 0.5s ease;
`;

export const ConfidenceLabel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

export const ConfidenceText = styled.span<{ $confidence: number }>`
  font-weight: 600;
  color: ${({ $confidence, theme }) =>
    $confidence < 40
      ? theme.colors.error
      : $confidence < 70
      ? theme.colors.blue
      : theme.colors.success};
`;

export const ConfidenceValue = styled.span`
  color: ${({ theme }) => theme.colors.text};
`;

export const SpecsList = styled.div`
  margin-top: 20px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

export const SpecItem = styled.div`
  background-color: ${({ theme }) => theme.colors.verae[50]};
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => theme.colors.text};
  border: 1px solid ${({ theme }) => theme.colors.border};
  word-break: break-word;
`;

export const DeviceTypeContainer = styled.div`
  display: flex;
  align-items: center;
`;

export const DeviceInfoHeader = styled.h2`
  color: ${({ theme }) => theme.colors.text};
  font-size: 1.2rem;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  font-weight: 600;
  width: 100%;
`;
